import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LuRefreshCw } from "react-icons/lu";
import axios from "../../axios";
import { fetchLatestVersion, runningVersion } from "../../helpers/appVersion";
import * as styled from "./UpdateWatcher.styled";

// Щоб після деплою всі гарантовано отримали нову версію (без Cmd+Shift+R):
// 1) раз на 5 хв і при поверненні на вкладку дивимось, чи на сервері новий index.html;
// 2) якщо так — оновлюємось при наступному переході між сторінками, або одразу, якщо вкладка у фоні;
//    плюс плашка «Оновити» внизу. Під час уроку/ДЗ самі не перезавантажуємо — лише плашка;
// 3) перезавантаження йде на унікальну адресу (?__v=…), тож старий кеш index.html не підхопиться;
// 4) кожен користувач повідомляє серверу свою версію — адмін бачить, хто вже оновився.
const CHECK_EVERY = 5 * 60 * 1000;
const BUSY_PATHS = ["/present/", "/do-homework/"];

// На сторінці є щось введене (відгук про урок, форма) — у фоні не перезавантажуємо, щоб не втратити.
// Дивимось просто на непорожні текстові поля: React синхронізує defaultValue з value, тож порівняння з ним не працює
const hasTypedInput = () =>
  [...document.querySelectorAll("textarea, input:not([type]), input[type=text], input[type=email], input[type=tel], input[type=number], input[type=password], input[type=url]")].some(
    (field) => field.value.trim() !== ""
  );

const hardReload = (version) => {
  const url = new URL(window.location.href);
  url.searchParams.set("__v", version || Date.now());
  window.location.replace(url.toString());
};

function UpdateWatcher() {
  const location = useLocation();
  const current = useRef(runningVersion());
  const [latest, setLatest] = useState(null);
  const lastCheck = useRef(0);
  const firstRoute = useRef(true);
  const outdated = Boolean(current.current && latest && latest !== current.current);
  const busy = BUSY_PATHS.some((path) => location.pathname.startsWith(path));

  // Прибираємо ?__v=… з адреси після оновлення
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("__v")) {
      url.searchParams.delete("__v");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
  }, []);

  const check = useCallback(async () => {
    if (!current.current || Date.now() - lastCheck.current < 30 * 1000) return;
    lastCheck.current = Date.now();
    try {
      const version = await fetchLatestVersion();
      if (version) setLatest(version);
    } catch (error) {}
  }, []);

  useEffect(() => {
    if (!current.current) return;
    const first = setTimeout(check, 5000);
    const timer = setInterval(check, CHECK_EVERY);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  // Вкладка у фоні — оновлюємось непомітно (крім уроку/ДЗ)
  useEffect(() => {
    if (!outdated || busy) return;
    const onHidden = () => {
      if (document.visibilityState === "hidden" && !hasTypedInput()) hardReload(latest);
    };
    if (document.visibilityState === "hidden" && !hasTypedInput()) {
      hardReload(latest);
      return;
    }
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [outdated, busy, latest]);

  // Перехід між сторінками — зручний момент оновитись (крім уроку/ДЗ)
  useEffect(() => {
    if (firstRoute.current) {
      firstRoute.current = false;
      return;
    }
    if (outdated && !busy) hardReload(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Звіт серверу: яка версія в цього користувача (раз на сесію для пари версія+токен)
  useEffect(() => {
    const version = current.current;
    const token = window.localStorage.getItem("token");
    if (!version || !token) return;
    const key = `clientVersionReported:${version}:${token.slice(-12)}`;
    try {
      if (window.sessionStorage.getItem(key)) return;
    } catch (error) {}
    axios
      .post("client-version", { version })
      .then(() => {
        try {
          window.sessionStorage.setItem(key, "1");
        } catch (error) {}
      })
      .catch(() => {});
  }, [location.pathname]);

  if (!outdated) return null;

  return (
    <styled.Toast role="status">
      <LuRefreshCw size={18} />
      <span>Платформа оновилась</span>
      <styled.Button type="button" onClick={() => hardReload(latest)}>
        Оновити
      </styled.Button>
    </styled.Toast>
  );
}

export default UpdateWatcher;
